import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Search, Globe, Zap, BarChart3, Bot, MessageSquare } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const auditComponents = [
  {
    icon: Search,
    title: "Google Rankings Analysis",
    description: "Complete analysis of your current Google rankings, opportunities, and technical issues",
  },
  {
    icon: Bot,
    title: "ChatGPT Visibility Check",
    description: "Test how often ChatGPT references or recommends your brand and products",
  },
  {
    icon: MessageSquare,
    title: "Perplexity Presence",
    description: "Analyze your visibility in Perplexity AI search results",
  },
  {
    icon: Globe,
    title: "Claude AI Coverage",
    description: "Assess how well Claude understands and recommends your offerings",
  },
  {
    icon: BarChart3,
    title: "Content Gap Analysis",
    description: "Identify topics your competitors cover that you're missing",
  },
  {
    icon: Zap,
    title: "Technical SEO Health",
    description: "Core Web Vitals, indexation, and technical foundation assessment",
  },
];

const whatYouGet = [
  "Full Google Search Console analysis",
  "AI search visibility score across ChatGPT, Perplexity, Claude",
  "Competitor AI visibility comparison",
  "Technical SEO health check",
  "Content gap opportunities",
  "Priority action items with estimated impact",
  "Custom GEO optimization roadmap",
];

const relatedInsights = [
  { title: "What is GEO?", slug: "what-is-geo" },
  { title: "How LLM Search Works", slug: "how-llm-search-works" },
  { title: "SEO for ChatGPT", slug: "seo-for-chatgpt" },
];

export default function AISEOGEOAudit() {
  return (
    <Layout>
      <SEO 
        title="Free AI SEO & GEO Audit | TheCloudSEO"
        description="Get a comprehensive audit of your visibility in Google and AI search engines like ChatGPT, Perplexity, and Claude. Free GEO assessment."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Resources", href: "/resources" },
              { label: "AI SEO & GEO Audit" },
            ]}
            className="mb-8"
          />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
                SEO + GEO Combined
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                Free AI SEO & <span className="text-gradient">GEO Audit</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Comprehensive visibility analysis across both traditional search and AI-powered 
                search engines. Know exactly where you stand and what to fix.
              </p>

              <ul className="space-y-3 mb-8">
                {whatYouGet.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/seo-geo-audit"
                data-cal-namespace="seo-geo-audit"
                data-cal-config='{"layout":"month_view"}'
              >
                Get Your Free Audit
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-sm text-muted-foreground mt-4">
                45-minute deep dive. Detailed report included.
              </p>
            </AnimatedSection>

            {/* What We Analyze */}
            <AnimatedSection delay={0.2}>
              <div className="bg-card rounded-3xl border border-border p-8">
                <h2 className="text-2xl font-bold mb-6">What We Analyze</h2>
                <div className="grid gap-4">
                  {auditComponents.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      viewport={{ once: true }}
                      className="flex gap-3 p-3 rounded-xl bg-secondary/50"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{item.title}</h3>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Why GEO Matters */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <h2 className="text-3xl font-bold mb-4">Why GEO Matters Now</h2>
              <p className="text-muted-foreground mb-6">
                AI search is rapidly changing how people find information. ChatGPT, Perplexity, 
                and Claude are becoming primary research tools for B2B buyers. If you're not 
                visible in these platforms, you're invisible to a growing audience.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { value: "40%", label: "of searches will be AI-first by 2025" },
                  { value: "65%", label: "of B2B buyers use AI for research" },
                  { value: "3x", label: "higher trust in AI recommendations" },
                  { value: "0%", label: "of brands optimizing for AI search" },
                ].map((stat, index) => (
                  <div key={index} className="p-4 rounded-xl bg-secondary/50 border border-border">
                    <div className="text-2xl font-bold text-primary">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-semibold mb-4">Related Insights</h3>
                <div className="space-y-3">
                  {relatedInsights.map((insight) => (
                    <Link
                      key={insight.slug}
                      to={`/insights/${insight.slug}`}
                      className="flex items-center justify-between p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                    >
                      <span className="font-medium">{insight.title}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Your Complete Search Visibility Picture
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Traditional SEO + AI Search optimization = Complete visibility strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/seo-geo-audit"
                data-cal-namespace="seo-geo-audit"
                data-cal-config='{"layout":"month_view"}'
              >
                Book Your Free Audit
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full"
                asChild
              >
                <Link to="/services/geo">
                  Learn About GEO Services
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
